/**
 * Audit-event persistence schema.
 *
 * Owning task: EX-XCUT-0047 (Pack 1.D — B-1.D.2).
 *
 * This is the canonical persistence layer for the audit-event taxonomy
 * defined in `shared/audit-events.ts`. It is purely additive — the
 * existing `audit_suspicious_flags` table (defined in credentials schema)
 * remains as the specialised store for suspicious-activity flags and is
 * NOT migrated into this table by Pack 1.
 *
 * Table relationship:
 *   - audit_events                — canonical taxonomy-keyed events
 *   - audit_suspicious_flags      — specialised investigation workflow rows
 *                                   (existing, unchanged in Pack 1)
 *
 * Discipline:
 *   - The `eventKey` column is FK-by-convention to the in-code taxonomy
 *     in `shared/audit-events.ts`. There is no DB-level FK because the
 *     taxonomy lives in code; the integrity invariant is enforced at the
 *     storage layer (server/storage/audit-events.ts).
 *   - The `signature` column carries the optional HMAC signature produced
 *     by `signCriticalAuditEntry`; required for events whose taxonomy entry
 *     has `requiresSignature: true`.
 *   - All rows are tenant-scoped via `tenantId`; platform-scoped events
 *     (super-admin actions outside any tenant context) use the well-known
 *     platform tenant id.
 */
import { pgTable, varchar, text, timestamp, jsonb, index, bigint } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

export const auditEvents = pgTable(
  "audit_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    eventKey: text("event_key").notNull(),
    category: text("category").notNull(),
    severity: text("severity").notNull(),
    actorId: text("actor_id"),
    actorRole: text("actor_role"),
    targetType: text("target_type"),
    targetId: text("target_id"),
    context: jsonb("context").$type<Record<string, unknown>>(),
    signature: text("signature"),
    // Task #1961 (Pack 10.F closure) — tamper-evident hash chain.
    // Each row references the previous row's `row_hash` via `prev_hash`,
    // and its own `row_hash` is HMAC-SHA-256 over the canonical payload.
    // See `server/engines/audit-chain.ts` for the algorithm and
    // `migrations/0136_audit_chain_tamper_evident.sql` for the columns.
    prevHash: text("prev_hash"),
    rowHash: text("row_hash"),
    chainPosition: bigint("chain_position", { mode: "number" }),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (t) => ({
    byTenantAndOccurred: index("audit_events_tenant_occurred_idx").on(t.tenantId, t.occurredAt),
    byTenantAndKey: index("audit_events_tenant_key_idx").on(t.tenantId, t.eventKey),
    byChain: index("audit_events_chain_idx").on(t.tenantId, t.chainPosition),
  }),
);

export const insertAuditEventSchema = createInsertSchema(auditEvents).omit({
  id: true,
  createdAt: true,
});
export type InsertAuditEvent = z.infer<typeof insertAuditEventSchema>;
export type AuditEvent = typeof auditEvents.$inferSelect;

/**
 * Task #1961 (Pack 10.F closure) — write-once chain checkpoints.
 *
 * Each row records the verified `row_hash` chain head for a given
 * `(table_name, tenant_id)` pair at a point in time, plus an optional
 * `external_ref` to the matching write-once object-storage entry. The
 * verifier (`scripts/verify-audit-chain.ts`) cross-checks the most
 * recent row in this table against the chain it derives from the
 * underlying audit table, then against the object-storage entry.
 */
export const auditChainCheckpoints = pgTable(
  "audit_chain_checkpoints",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tableName: text("table_name").notNull(),
    tenantId: text("tenant_id").notNull(),
    chainPosition: bigint("chain_position", { mode: "number" }).notNull(),
    headDigest: text("head_digest").notNull(),
    rowCount: bigint("row_count", { mode: "number" }).notNull(),
    signedCount: bigint("signed_count", { mode: "number" }).notNull(),
    unsignedCount: bigint("unsigned_count", { mode: "number" }).notNull(),
    tamperedCount: bigint("tampered_count", { mode: "number" }).notNull().default(0),
    externalRef: text("external_ref"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (t) => ({
    byTableTenant: index("audit_chain_checkpoints_table_tenant_idx").on(
      t.tableName,
      t.tenantId,
      t.createdAt,
    ),
  }),
);

export const insertAuditChainCheckpointSchema = createInsertSchema(auditChainCheckpoints).omit({
  id: true,
  createdAt: true,
});
export type InsertAuditChainCheckpoint = z.infer<typeof insertAuditChainCheckpointSchema>;
export type AuditChainCheckpoint = typeof auditChainCheckpoints.$inferSelect;
